import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogService } from '../../services/blogService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Star, StarOff, 
  Save, X, ArrowLeft, Check, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import { companyInfo } from '../../data/mock';

const BlogAdminPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Educación Financiera',
    image: '',
    read_time: '5 min',
    featured: false,
    published: false,
    author: 'FACTORYZA'
  });

  const categories = [
    'Educación Financiera',
    'Comparativas',
    'Costos',
    'Industrias',
    'Consejos',
    'Noticias',
    'Casos de Éxito'
  ];

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await blogService.getAdminPosts({ perPage: 50 });
      setPosts(data.posts);
    } catch (err) {
      toast.error('Error al cargar los artículos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'Educación Financiera',
      image: '',
      read_time: '5 min',
      featured: false,
      published: false,
      author: 'FACTORYZA'
    });
    setEditingPost(null);
  };

  const handleNewPost = () => {
    resetForm();
    setShowEditor(true);
  };

  const handleEditPost = (post) => {
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image: post.image || '',
      read_time: post.read_time,
      featured: post.featured,
      published: post.published,
      author: post.author
    });
    setEditingPost(post);
    setShowEditor(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      setSaving(true);
      
      if (editingPost) {
        await blogService.updatePost(editingPost.id, formData);
        toast.success('Artículo actualizado correctamente');
      } else {
        await blogService.createPost(formData);
        toast.success('Artículo creado correctamente');
      }
      
      setShowEditor(false);
      resetForm();
      loadPosts();
    } catch (err) {
      toast.error('Error al guardar el artículo');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${post.title}"?`)) {
      return;
    }

    try {
      await blogService.deletePost(post.id);
      toast.success('Artículo eliminado');
      loadPosts();
    } catch (err) {
      toast.error('Error al eliminar el artículo');
      console.error(err);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await blogService.updatePost(post.id, { published: !post.published });
      toast.success(post.published ? 'Artículo despublicado' : 'Artículo publicado');
      loadPosts();
    } catch (err) {
      toast.error('Error al actualizar el artículo');
      console.error(err);
    }
  };

  const handleToggleFeatured = async (post) => {
    try {
      await blogService.updatePost(post.id, { featured: !post.featured });
      toast.success(post.featured ? 'Quitado de destacados' : 'Marcado como destacado');
      loadPosts();
    } catch (err) {
      toast.error('Error al actualizar el artículo');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img
                  src={companyInfo.logoBlack}
                  alt="FACTORYZA"
                  className="h-10 w-auto"
                />
              </Link>
              <span className="text-gray-300">|</span>
              <h1 className="font-semibold text-gray-900">Admin Blog</h1>
            </div>
            <nav className="flex items-center gap-4">
              <Link to="/blog" className="text-gray-600 hover:text-[#0092ca] font-medium">
                Ver Blog
              </Link>
              <Link to="/" className="text-gray-600 hover:text-[#0092ca] font-medium">
                Inicio
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showEditor ? (
          <>
            {/* Actions Bar */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Artículos del Blog</h2>
                <p className="text-gray-500">{posts.length} artículos en total</p>
              </div>
              <Button 
                onClick={handleNewPost}
                className="bg-[#0092ca] hover:bg-[#007bb5] text-white rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nuevo Artículo
              </Button>
            </div>

            {/* Posts Table */}
            {loading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#0092ca] border-t-transparent mx-auto" />
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay artículos</h3>
                <p className="text-gray-500 mb-6">Comienza creando tu primer artículo del blog.</p>
                <Button 
                  onClick={handleNewPost}
                  className="bg-[#0092ca] hover:bg-[#007bb5] text-white rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Crear Artículo
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Título</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Categoría</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Estado</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Fecha</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {posts.map((post) => (
                      <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {post.featured && (
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900 line-clamp-1">{post.title}</p>
                              <p className="text-sm text-gray-500 line-clamp-1">{post.excerpt}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                            {post.category}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {post.published ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                              <Check className="w-3 h-3" />
                              Publicado
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">
                              Borrador
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(post.published_at || post.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleFeatured(post)}
                              className={`p-2 rounded-lg transition-colors ${
                                post.featured 
                                  ? 'text-yellow-500 hover:bg-yellow-50' 
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={post.featured ? 'Quitar destacado' : 'Destacar'}
                            >
                              {post.featured ? <Star className="w-5 h-5 fill-current" /> : <StarOff className="w-5 h-5" />}
                            </button>
                            <button
                              onClick={() => handleTogglePublish(post)}
                              className={`p-2 rounded-lg transition-colors ${
                                post.published 
                                  ? 'text-green-500 hover:bg-green-50' 
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={post.published ? 'Despublicar' : 'Publicar'}
                            >
                              {post.published ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                            </button>
                            <button
                              onClick={() => handleEditPost(post)}
                              className="p-2 text-gray-400 hover:text-[#0092ca] hover:bg-blue-50 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(post)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          /* Editor */
          <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => { setShowEditor(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-500" />
                </button>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingPost ? 'Editar Artículo' : 'Nuevo Artículo'}
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowEditor(false); resetForm(); }}
                  className="rounded-full"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-[#0092ca] hover:bg-[#007bb5] text-white rounded-full"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Guardando...
                    </span>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </>
                  )}
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-medium text-gray-700">Título *</label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="¿Qué es el Factoring y Cómo Funciona?"
                    className="text-lg"
                    required
                  />
                </div>

                {/* Excerpt */}
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-medium text-gray-700">Extracto / Resumen *</label>
                  <Textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Breve descripción que aparecerá en las tarjetas del blog..."
                    rows={2}
                    required
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Categoría</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0092ca]/20 focus:border-[#0092ca]"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Read Time */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Tiempo de lectura</label>
                  <Input
                    name="read_time"
                    value={formData.read_time}
                    onChange={handleInputChange}
                    placeholder="5 min"
                  />
                </div>

                {/* Image URL */}
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-medium text-gray-700">URL de Imagen</label>
                  <Input
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://images.unsplash.com/..."
                  />
                  {formData.image && (
                    <img 
                      src={formData.image} 
                      alt="Preview" 
                      className="mt-2 h-32 w-auto rounded-lg object-cover"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-medium text-gray-700">Contenido del Artículo * (HTML)</label>
                  <Textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="<h2>Introducción</h2><p>El factoring es...</p>"
                    rows={15}
                    className="font-mono text-sm"
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Puedes usar HTML para dar formato: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, etc.
                  </p>
                </div>

                {/* Author */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Autor</label>
                  <Input
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="FACTORYZA"
                  />
                </div>

                {/* Checkboxes */}
                <div className="flex items-center gap-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="published"
                      checked={formData.published}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-[#0092ca] focus:ring-[#0092ca]"
                    />
                    <span className="text-sm font-medium text-gray-700">Publicar</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-[#0092ca] focus:ring-[#0092ca]"
                    />
                    <span className="text-sm font-medium text-gray-700">Destacado</span>
                  </label>
                </div>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default BlogAdminPage;
